/**
 * @typedef {import("react-ace/lib").ICommand['exec']} ICommandExecFunction
 * @typedef {import("ace-builds/ace").Ace.Editor} Ace.Editor
 * @typedef {import("ace-builds/ace").Ace.Range} Ace.Range
 * @typedef {import("react-ace/lib").ICommand} ICommand
 */

const unicodeCombiningGraveAccent = '\u0300';
const unicodeCombiningAcuteAccent = '\u0301';
const unicodeCombiningDiaresis = '\u0308';
const combiningMap = {
  "`": unicodeCombiningGraveAccent,
  "'": unicodeCombiningAcuteAccent,
  "\"": unicodeCombiningDiaresis,
}

/**
 * 
 * @param {string} letter 
 */
const normalizeAsh = (letter) => {
    return {
      'Æ': 'AE',
      'Œ': 'OE',
      'æ': 'ae',
      'œ': 'oe',
    }[letter];
}

/**
 * 
 * @param {Ace.Editor} editor
 * @param {(previousLetter: string, range: Ace.Range) => boolean | undefined} callback 
 * @param {() => void} defaultAction
 */
const operateOnEmptyRange = (editor, callback, defaultAction) => {
  const range = editor.selection.getRange();
  if (range.isEmpty()) {
    const document = editor.getSession().getDocument();
    // check to make sure we are outside of parentheses:
    const priorToCursor = document.getTextRange(range.extend(0, 0));
    var lastOpenParen = priorToCursor.lastIndexOf('(');
    var lastCloseParen = priorToCursor.lastIndexOf(')');
    if(lastCloseParen < lastOpenParen) {
      defaultAction();
      return;
    }

    const previousLetterRange = range.extend(range.start.row, range.start.column - 1);
    const previousLetter = document.getTextRange(previousLetterRange)
    if (callback(previousLetter, previousLetterRange)) {
      return;
    }
  }
  defaultAction();
}

/**
 * 
 * @param {Ace.Editor} editor 
 * @param {keyof typeof combiningMap} character 
 */
const handleCombiningCharacter = (editor, character) => {
  operateOnEmptyRange(editor, (previousLetter, range) => {
    const newText = (previousLetter + combiningMap[character]).normalize();
    if (newText.length === 1 || /[aeiouyæœ]/i.test(previousLetter)) {
      editor.selection.setRange(range);
      editor.insert(newText);
      return true;
    }
  }, () => editor.insert(character, false));
}

/**
 * @type ICommandExecFunction
 */
export const handleApostropheOrAcuteAccent = (editor, args) =>
  handleCombiningCharacter(editor, "'");

/**
 * @type ICommandExecFunction
 */
export const handleDiaresisOrQuotation = (editor, args) =>
  handleCombiningCharacter(editor, "\"");

/**
 * @type ICommandExecFunction
 */
export const handleGraveAccentOrBackTick = (editor, args) =>
  handleCombiningCharacter(editor, "`");

/**
 * @type ICommandExecFunction
 */
export const handleBackspace = (editor, args) => {
  operateOnEmptyRange(editor, (previousLetter, range) => {
    const combiningEntries = Object.entries(combiningMap);
    const previousLetterWithCombining =
      normalizeAsh(previousLetter) ||
      combiningEntries.reduce(
        (val, [key, combining]) => val.replace(combining, key),
        previousLetter.normalize("NFD")
      );
    if (previousLetterWithCombining.length > 1) {
      editor.selection.setRange(range);
      editor.insert(previousLetterWithCombining);
      return true;
    }
  }, () => editor.remove('left'));
}

/**
 * @type ICommandExecFunction
 */
export const handleLetter_e = (editor, args) => handleE(editor);
export const handleLetter_E = (editor, args) => handleE(editor, 'E');

/**
 * 
 * @param {Ace.Editor} editor 
 * @param {'e'|'E'} e 
 */
const handleE = (editor, e = 'e') => {
  operateOnEmptyRange(editor, (previousLetter, range) => {
    if (/[ao]/i.test(previousLetter)) {
      let newLetter = '';
      if (previousLetter.toUpperCase() === previousLetter) {
        // We require the capitalization of the E to match the capitalization of the previous letter:
        if (e === 'e') return;
        newLetter = previousLetter === 'A' ? 'Æ' : 'Œ';
      } else {
        if (e === 'E') return;
        newLetter = previousLetter === 'a' ? 'æ' : 'œ';
      }
      editor.selection.setRange(range);
      editor.insert(newLetter);
      return true;
    }
  }, () => editor.insert(e, false));
}

/**
 * @type ICommand[]
 */
export const gabcAceCustomCommands = [{
  name: 'Erase the selection or previous accent',
  bindKey: { win: "Backspace", mac: "Backspace" },
  exec: handleBackspace,
}, {
  name: 'Add acute accent or apostrophe',
  bindKey: { win: "'", mac: "'" },
  exec: handleApostropheOrAcuteAccent,
}, {
  name: 'Add diaresis or quotation',
  bindKey: { win: "shift+\"", mac: "shift+\"" },
  exec: handleDiaresisOrQuotation,
}, {
  name: 'Add grave accent or backtick',
  bindKey: { win: "`", mac: "`" },
  exec: handleGraveAccentOrBackTick,
}, {
  name: 'Add e',
  bindKey: { win: "e", mac: "e" },
  exec: handleLetter_e,
}, {
  name: 'Add E',
  bindKey: { win: "shift+e", mac: "shift+e" },
  exec: handleLetter_E,
}];